// 共享工具函数

import { DIFFICULTY_LEVELS, KNOWLEDGE_POINT_DIFFICULTIES, IMPORTANCE_LEVELS } from '../constants';

/**
 * 生成唯一ID
 */
export function generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * 格式化日期
 */
export function formatDate(date: string | Date): string {
    const d = new Date(date);
    return d.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * 格式化文件大小
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * 计算内容哈希值
 */
export async function calculateContentHash(content: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(content);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * 验证文件类型
 */
export function isValidFileType(filename: string, allowedTypes: string[]): boolean {
    const extension = filename.split('.').pop()?.toLowerCase();
    return extension ? allowedTypes.includes(extension) : false;
}

/**
 * 验证文件大小
 */
export function isValidFileSize(size: number, maxSize: number): boolean {
    return size <= maxSize;
}

/**
 * 获取难度级别显示名称
 */
export function getDifficultyDisplayName(difficulty: string): string {
    const difficultyMap: Record<string, string> = {
        [DIFFICULTY_LEVELS.BEGINNER]: '初级',
        [DIFFICULTY_LEVELS.INTERMEDIATE]: '中级',
        [DIFFICULTY_LEVELS.ADVANCED]: '高级',
    };
    return difficultyMap[difficulty] || difficulty;
}

/**
 * 获取知识点难度显示名称
 */
export function getKnowledgePointDifficultyName(difficulty: string): string {
    const difficultyMap: Record<string, string> = {
        [KNOWLEDGE_POINT_DIFFICULTIES.EASY]: '简单',
        [KNOWLEDGE_POINT_DIFFICULTIES.MEDIUM]: '中等',
        [KNOWLEDGE_POINT_DIFFICULTIES.HARD]: '困难',
    };
    return difficultyMap[difficulty] || difficulty;
}

/**
 * 获取重要性级别显示名称
 */
export function getImportanceLevelName(level: number): string {
    const levelMap: Record<number, string> = {
        [IMPORTANCE_LEVELS.VERY_LOW]: '极低',
        [IMPORTANCE_LEVELS.LOW]: '低',
        [IMPORTANCE_LEVELS.MEDIUM]: '中等',
        [IMPORTANCE_LEVELS.HIGH]: '高',
        [IMPORTANCE_LEVELS.VERY_HIGH]: '极高',
    };
    return levelMap[level] || level.toString();
}

/**
 * 计算相似度
 */
export function calculateSimilarity(text1: string, text2: string): number {
    if (text1 === text2) return 1.0;
    if (text1.length === 0 || text2.length === 0) return 0.0;

    const words1 = new Set(text1.toLowerCase().split(/\s+/));
    const words2 = new Set(text2.toLowerCase().split(/\s+/));

    const intersection = new Set([...words1].filter(x => words2.has(x)));
    const union = new Set([...words1, ...words2]);

    return intersection.size / union.size;
}

/**
 * 防抖函数
 */
export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

/**
 * 节流函数
 */
export function throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
): (...args: Parameters<T>) => void {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

/**
 * 深拷贝对象
 */
export function deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
    if (obj instanceof Array) return obj.map(item => deepClone(item)) as unknown as T;
    if (typeof obj === 'object') {
        const clonedObj = {} as T;
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                clonedObj[key] = deepClone(obj[key]);
            }
        }
        return clonedObj;
    }
    return obj;
}

/**
 * 验证邮箱格式
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * 验证URL格式
 */
export function isValidUrl(url: string): boolean {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
}

/**
 * 生成随机字符串
 */
export function generateRandomString(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}
